import accounting from 'accounting';
import { forEach, compact, divide, endsWith, flow, join, startCase, startsWith } from 'lodash';
import sanitizeFilename from 'sanitize-filename';
import stripTags from 'striptags';
import xss from 'xss';
import { isUUID } from './validate';

export const concat = (...args) => join(args, '');

export const extractExcerpt = (str?: string, len: number = 128, suffix: string = ' ...') => {
  const pos = String(str).indexOf(' ', len);
  return `${String(str).substring(0, pos > 0 ? pos : len)}${pos > 0 ? suffix : ''}`;
};

export const formatAsBytes = (obj?: number) => {
  const num = Number(obj);
  const kilo = 1024;
  const decimals = 2;
  const dimensions = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(num) / Math.log(kilo));
  const val = parseFloat((num / kilo ** i).toFixed(decimals));
  return `${val} ${dimensions[i]}`;
};

export const formatAsDuration = (obj?: number) => {
  const d = parseInt(Number(obj), 10);
  let h = Math.floor(d / 3600);
  let m = Math.floor((d - h * 3600) / 60);
  let s = d - h * 3600 - m * 60;
  if (h < 10) {
    h = `0${h}`;
  }
  if (m < 10) {
    m = `0${m}`;
  }
  if (s < 10) {
    s = `0${s}`;
  }
  let time = '';
  if (h === '00') {
    time = `${m}:${s}`;
  } else {
    time = `${h}:${m}:${s}`;
  }
  return time;
};

export const formatAsDurationDescriptive = (obj?: number) => {
  const d = Number(obj);
  const h = Math.floor(d / 3600);
  const m = Math.floor((d % 3600) / 60);
  const s = Math.floor((d % 3600) % 60);
  const hDisplay = h > 0 ? h + (h === 1 ? ' hour, ' : ' hours, ') : '';
  const mDisplay = m > 0 ? m + (m === 1 ? ' minute, ' : ' minutes, ') : '';
  const sDisplay = s > 0 ? s + (s === 1 ? ' second' : ' seconds') : '';
  return `${hDisplay}${mDisplay}${sDisplay}`;
};

export const formatAsHtml = (str?: string) => String(str).replace(/(?:\r\n|\r|\n)/g, '<br/>');

export const formatAsMoney = (
  obj?: number,
  curr: string = '$',
  dec: number = 2,
  sepThou: string = ',',
  sepDeci: string = '.'
) => accounting.formatMoney(obj, curr, dec, sepThou, sepDeci);

export const formatAsNumber = (obj?: number) => accounting.formatNumber(obj);

export const formatAsTitle = (str: string = '') => startCase(str);

export const getInitials = (text: string, limit: number = 3, separator: string = ' ') => {
  let output = '';
  if (!text) {
    return output;
  }

  const words = text.split(separator);
  for (let i = 0; i < limit; i += 1) {
    output = `${output}${words[i]?.charAt(0) || ''}`;
  }
  return output.toUpperCase();
};

export const getTrail = (str?: string, separator?: string, prepend: boolean = false) =>
  `${prepend ? separator || '' : ''}${String(str).split(separator).pop()}`;

export const obscure = (str?: string) => {
  if (!str) {
    return str;
  }
  return str.length > 2
    ? `${str[0]}${new Array(str.length - 1).join('*')}${str[str.length - 1]}`
    : `${str[0]}${new Array(str.length).join('*')}`;
};

export const obscureEmail = (email?: string) => {
  const [name, domain] = email.split('@');
  return `${obscure(name)}@${domain}`;
};

export const processAcronyms = (str: string = '') =>
  str?.replace(/email/g, 'E-Mail').replace(/sms/g, 'SMS');

export const replaceTags = (str?: string, replacement: string = '') =>
  String(str)
    .replace(/(<([^>]+)>)/gi, replacement)
    .replace(/&nbsp;/g, '');

export const replaceEnumUnsafe = (str?: string) => String(str).replace(/[^a-zA-Z0-9_]+/gi, '_');
export const replaceFileUnsafe = (str?: string) => sanitizeFilename(String(str));
export const replaceNonNumbers = (str?: string) => String(str).replace(/[\s]|[^0-9]/g, '');
export const replaceTagsUnsafe = (str?: string) => stripTags(String(str));
export const replaceXMLUnsafe = (str?: string) => String(str).replace(/[&<>'"]/g, '');

export const addLead = (str?: string, prefix?: string) =>
  startsWith(str, prefix) ? str : `${prefix}${str}`;
export const removeLead = (str?: string, prefix?: string) =>
  startsWith(str, prefix) ? str.substr(prefix.length) : str;
export const removeLeading = (str?: string, prefix?: string) =>
  str === removeLead(str, prefix) ? str : removeLeading(removeLead(str, prefix), prefix);

export const addTrail = (str?: string, suffix?: string) =>
  endsWith(str, suffix) ? str : `${str}${suffix}`;
export const removeTrail = (str?: string, suffix?: string) =>
  endsWith(str, suffix) ? str.substr(0, str.length - suffix.length) : str;
export const removeTrailing = (str?: string, suffix?: string) =>
  str === removeTrail(str, suffix) ? str : removeTrailing(removeTrail(str, suffix), suffix);

export const round = (value: number = 0) => {
  if (!value) {
    return 0;
  }
  return divide(Math.round(value * 1e2), 1e2);
};

export const underscoresToWhitespace = (str: string = '') => str?.replace(/_/g, ' ');
export const whitespaceToDashes = (str: string = '') => str?.replace(/ /g, '-');
export const unFormatMoney = (str?: string) => accounting.unformat(str);

export const sanitize = flow([underscoresToWhitespace, processAcronyms, formatAsTitle]);
export const sanitizeHtml = (str) => {
  if (!str) {
    return '';
  }

  let output = String(str)
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/( class=(")?Mso[a-zA-Z]+(")?| class=(")?[a-zA-Z0-9]+(")?)/gi, ' ')
    .replace(/<!--(.*?)-->/gi, '')
    .replace(/<!(.*?)>/gi, '')
    .replace(
      /<(\/)*(html|head|body|meta|link|button|xml|\?xml:|st1:|o:|v:|w:|font)(.*?)>/gi,
      ''
    );
  forEach(
    ['html', 'head', 'style', 'script', 'applet', 'embed', 'noframes', 'noscript'],
    (t) => {
      output = output.replace(new RegExp(`<${t}(?:.|\\n)*?${t}(.*?)>`, 'gi'), '');
    }
  );
  forEach(['style', 'start'], (a) => {
    output = output.replace(new RegExp(` ${a}="(.*?)"`, 'gi'), '');
  });

  return xss(output);
};

export const sanitizeTooltip = (str: string = '') => {
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
    '{': '',
    '}': ''
  };
  return str.replace(/[&<>"'/{}]/gi, (match) => map[match]);
};

export const toCamelCase = (str?: string) =>
  str.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase());

export const toUUID = (str?: string) => (isUUID(str) ? str : undefined);

export const wordCount = (str?: string) => (str ? compact(str.split(/\s+/)).length : 0);

export default {
  addLead,
  addTrail,
  concat,
  extractExcerpt,
  formatAsBytes,
  formatAsDuration,
  formatAsDurationDescriptive,
  formatAsHtml,
  formatAsMoney,
  formatAsNumber,
  formatAsTitle,
  getInitials,
  getTrail,
  obscure,
  obscureEmail,
  processAcronyms,
  replaceTags,
  replaceEnumUnsafe,
  replaceFileUnsafe,
  replaceNonNumbers,
  replaceXMLUnsafe,
  removeLead,
  removeLeading,
  removeTrail,
  removeTrailing,
  round,
  sanitize,
  sanitizeHtml,
  sanitizeTooltip,
  toCamelCase,
  toUUID,
  wordCount,
  underscoresToWhitespace,
  whitespaceToDashes,
  unFormatMoney
};
